import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import InputAdornment from "@mui/material/InputAdornment";
import { useDispatch, useSelector } from "react-redux";
import {
  addTeamMember,
  clearSuccessMessage,
} from "../../../redux/Slice/Settings/addfunctionsSlice";
import { fetchTeamMembers, fetchRolesWithUsage } from "../../../redux/Slice/Settings/settingsSlice";
import { toast } from "react-hot-toast";
import { checkBeforeOutbound } from "../../../utils/subscriptionError";
import { getAuthToken } from "../../../utils/getAuthToken";

/** Dark mode only: override Chrome :-webkit-autofill so it matches MUI outlined fields */
const addMemberOutlinedFieldSx = (theme) => {
  const base = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
    },
  };
  if (theme.palette.mode !== "dark") {
    return base;
  }
  const surface = theme.palette.background.paper;
  const surfaceFocus = theme.palette.grey[800];
  const ink = theme.palette.text.primary;
  return {
    ...base,
    "& .MuiOutlinedInput-input:-webkit-autofill": {
      WebkitBoxShadow: `0 0 0 1000px ${surface} inset !important`,
      WebkitTextFillColor: `${ink} !important`,
      caretColor: ink,
      boxShadow: `0 0 0 1000px ${surface} inset !important`,
      transition: "background-color 99999s ease-out 0s",
    },
    "& .MuiOutlinedInput-input:-webkit-autofill:hover": {
      WebkitBoxShadow: `0 0 0 1000px ${surfaceFocus} inset !important`,
      boxShadow: `0 0 0 1000px ${surfaceFocus} inset !important`,
      WebkitTextFillColor: `${ink} !important`,
    },
    "& .MuiOutlinedInput-input:-webkit-autofill:focus": {
      WebkitBoxShadow: `0 0 0 1000px ${surfaceFocus} inset !important`,
      boxShadow: `0 0 0 1000px ${surfaceFocus} inset !important`,
      WebkitTextFillColor: `${ink} !important`,
    },
    "& .MuiOutlinedInput-input:-webkit-autofill:active": {
      WebkitBoxShadow: `0 0 0 1000px ${surface} inset !important`,
      boxShadow: `0 0 0 1000px ${surface} inset !important`,
      WebkitTextFillColor: `${ink} !important`,
    },
  };
};

const AddTeamMembers = ({ onClose, open }) => {
  const dispatch = useDispatch();
  const { loading, error, successMessage } = useSelector(
    (state) => state.addFunctions
  );
  const { rolesWithUsage } = useSelector((state) => state.settings);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    roleName: "",
  });

  const [errors, setErrors] = useState({});
  const [passwordChecks, setPasswordChecks] = useState({
    length: false,
    lowercase: false,
    uppercase: false,
    digit: false,
    special: false,
  });
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "",
  });

  // Fetch roles when component mounts
  useEffect(() => {
    dispatch(fetchRolesWithUsage());
  }, [dispatch]);

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Reset form when dialog opens (only when transitioning from closed to open)
  const prevOpenRef = React.useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      // Dialog just opened - reset form
      setFormData({
        name: "",
        email: "",
        password: "",
        confirmPassword: "",
        roleName: "",
      });
      setErrors({});
      setPasswordFocused(false);
      setShowPassword(false);
      setShowConfirmPassword(false);
      dispatch(clearSuccessMessage());
    }
    prevOpenRef.current = open;
  }, [open, dispatch]);

  // Close dialog and reset form on success (toast is shown by the parent TeamMembers)
  useEffect(() => {
    if (successMessage) {
      dispatch(fetchTeamMembers());
      setFormData({ name: "", email: "", password: "", confirmPassword: "", roleName: "" });
      setErrors({});
      setPasswordFocused(false);
      setShowPassword(false);
      setShowConfirmPassword(false);
      onClose();
    }
  }, [successMessage, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (error) {
      setSnackbar({ open: true, message: error, severity: "error" });
    }
  }, [error]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (name === "password") {
      setPasswordChecks({
        length: value.length >= 8,
        lowercase: /[a-z]/.test(value),
        uppercase: /[A-Z]/.test(value),
        digit: /[0-9]/.test(value),
        special: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(value),
      });
    }
  };

  const validate = () => {
    const tempErrors = {};
    if (!formData.name) tempErrors.name = "Name is required.";
    if (!formData.email) tempErrors.email = "Email is required.";
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      tempErrors.email = "Invalid email.";
    if (!formData.password) {
      tempErrors.password = "Password is required.";
    } else {
      const unmet = [];
      if (!passwordChecks.length) unmet.push("at least 8 characters");
      if (!passwordChecks.lowercase) unmet.push("one lowercase letter");
      if (!passwordChecks.uppercase) unmet.push("one uppercase letter");
      if (!passwordChecks.digit) unmet.push("one digit");
      if (!passwordChecks.special) unmet.push("one special character");
      if (unmet.length) {
        tempErrors.password = `Password must contain ${unmet.join(", ")}.`;
      }
    }
    if (formData.password !== formData.confirmPassword)
      tempErrors.confirmPassword = "Passwords do not match.";
    if (!formData.roleName) tempErrors.roleName = "Role Name is required.";

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleCreate = async () => {
    const { blocked, message: blockMsg } = await checkBeforeOutbound(getAuthToken, ['teamMembersCreated']);
    if (blocked) { toast.error(blockMsg, { id: 'subscription-blocked', duration: 5000 }); return; }
    if (validate()) {
      dispatch(addTeamMember(formData));
    }
  };

  const handleModalClose = () => {
    setFormData({
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
      roleName: "",
    });
    setErrors({});
    setPasswordFocused(false);
    setShowPassword(false);
    setShowConfirmPassword(false);
    dispatch(clearSuccessMessage());
    onClose();
  };

  return (
    <>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: "bold",
          fontSize: "24px",
          padding: "14px",
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          backgroundColor: (theme) => theme.palette.background.paper,
        }}
      >
        <Typography variant="h6">Add New Team Member</Typography>
        <IconButton onClick={handleModalClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: "24px",
          backgroundColor: (theme) => theme.palette.background.paper,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
            padding: 1,
          }}
        >
          <TextField
            label="Name"
            name="name"
            variant="outlined"
            fullWidth
            value={formData.name}
            onChange={handleChange}
            error={!!errors.name}
            helperText={errors.name}
            sx={addMemberOutlinedFieldSx}
          />
          <TextField
            label="Email"
            name="email"
            type="email"
            variant="outlined"
            fullWidth
            value={formData.email}
            onChange={handleChange}
            error={!!errors.email}
            helperText={errors.email}
            autoComplete="email"
            inputProps={{
              spellCheck: false,
              autoCapitalize: "none",
              autoCorrect: "off",
              maxLength: 254,
            }}
            sx={addMemberOutlinedFieldSx}
          />
          <TextField
            label="Password"
            name="password"
            type={showPassword ? "text" : "password"}
            variant="outlined"
            fullWidth
            value={formData.password}
            onChange={handleChange}
            onFocus={() => setPasswordFocused(true)}
            error={!!errors.password}
            helperText={errors.password}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword((v) => !v)}
                    edge="end"
                    size="small"
                    sx={{ color: "text.secondary" }}
                  >
                    {showPassword ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={addMemberOutlinedFieldSx}
          />
          {/* Password requirements — shown on focus, hidden once all are met */}
          {passwordFocused && !Object.values(passwordChecks).every(Boolean) && (
            <Box
              sx={(theme) => ({
                p: "12px 16px",
                borderRadius: "10px",
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.mode === "dark" ? "#1a1a1a" : "#f9fafb",
              })}
            >
              {[
                { key: "length", label: "At least 8 characters" },
                { key: "lowercase", label: "At least one lowercase letter" },
                { key: "uppercase", label: "At least one uppercase letter" },
                { key: "digit", label: "At least one number" },
                { key: "special", label: "At least one special character" },
              ].map((item) => (
                <Box
                  key={item.key}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    py: "3px",
                  }}
                >
                  {passwordChecks[item.key] ? (
                    <CheckCircleOutlineIcon sx={{ fontSize: 17, color: "success.main" }} />
                  ) : (
                    <RadioButtonUncheckedIcon sx={(theme) => ({ fontSize: 17, color: theme.palette.mode === "dark" ? "#555" : "#bbb" })} />
                  )}
                  <Typography
                    sx={(theme) => ({
                      fontSize: "14px",
                      color: passwordChecks[item.key]
                        ? (theme.palette.mode === "dark" ? "#4ade80" : "#16a34a")
                        : theme.palette.text.secondary,
                      fontWeight: passwordChecks[item.key] ? 500 : 400,
                    })}
                  >
                    {item.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
          <TextField
            label="Confirm Password"
            name="confirmPassword"
            type={showConfirmPassword ? "text" : "password"}
            variant="outlined"
            fullWidth
            value={formData.confirmPassword}
            onChange={handleChange}
            disabled={!Object.values(passwordChecks).every(Boolean)}
            error={!!errors.confirmPassword || (formData.confirmPassword.length > 0 && formData.confirmPassword !== formData.password)}
            helperText={errors.confirmPassword || (formData.confirmPassword.length > 0 && formData.confirmPassword !== formData.password ? "Passwords do not match" : "")}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword((v) => !v)}
                    edge="end"
                    size="small"
                    disabled={!Object.values(passwordChecks).every(Boolean)}
                    sx={{ color: "text.secondary" }}
                  >
                    {showConfirmPassword ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={addMemberOutlinedFieldSx}
          />
          <FormControl fullWidth error={!!errors.roleName}>
            <InputLabel>Role</InputLabel>
            <Select
              name="roleName"
              value={formData.roleName}
              onChange={handleChange}
              label="Role"
              sx={{
                borderRadius: "12px",
              }}
            >
              {rolesWithUsage
                .filter(role => role.roleName !== "Owner")
                .map((role) => (
                  <MenuItem key={role._id} value={role.roleName}>
                    {role.roleName}
                  </MenuItem>
                ))}
            </Select>
            {errors.roleName && (
              <Typography variant="caption" color="error">
                {errors.roleName}
              </Typography>
            )}
          </FormControl>
        </Box>
      </DialogContent>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          padding: "16px 24px",
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          backgroundColor: (theme) => theme.palette.background.paper,
        }}
      >
        <Button
          variant="outlined"
          onClick={handleModalClose}
          sx={{
            borderRadius: "20px",
            textTransform: "capitalize",
            borderColor: (theme) => theme.palette.divider,
            color: (theme) => theme.palette.text.primary,
            "&:hover": {
              borderColor: (theme) => theme.palette.primary.main,
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={loading}
          sx={{
            borderRadius: "20px",
            textTransform: "capitalize",
            backgroundColor: (theme) => theme.palette.primary.main,
            "&:hover": {
              backgroundColor: (theme) => theme.palette.primary.dark,
            },
          }}
        >
          {loading ? "Creating..." : "Create"}
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddTeamMembers;
